import { useOrganiser, useSupplier } from "hooks";
import { useSelector } from "react-redux";
import { selectMetadata } from "store/metadata/selectors";
import { selectUser } from "store/auth/selectors";
import { useMemo } from "react";
import { IShow, PermissionPackage } from "@evennt-tech/api-v2-core";
import {
  useOrganiserStripeConnectAccounts,
  useSupplierStripeConnectAccount,
} from "hooks/useStripeConnectAccounts";

type UseShowStripeConnectBarArgs = {
  shows?: IShow[];
};

const useShowStripeConnectBar = (
  params: UseShowStripeConnectBarArgs
): boolean => {
  const { shows } = params;
  const { role } = useSelector(selectUser);
  const { hideSidebar } = useSelector(selectMetadata);
  const [organiser] = useOrganiser();
  const [supplier] = useSupplier();

  const supplierCompanyId =
    supplier?.supplierCompanyToSuppliers?.[0]?.supplierCompanyId;
  const organiserCompanyId =
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompanyId;
  const organiserPermissionPackages = organiser?.permissionPackages;

  const organiserHasVendrPackage = Boolean(
    organiserPermissionPackages?.includes(PermissionPackage.VENDR)
  );

  // Cached + deduped (react-query). Only the active role's query actually
  // fires (`enabled` gate); both hooks are still called unconditionally to
  // satisfy the rules of hooks.
  const {
    accounts,
    isFetched: organiserAccountsFetched,
  } = useOrganiserStripeConnectAccounts(
    organiserCompanyId,
    role === "organiser" && organiserHasVendrPackage
  );
  const {
    account: supplierAccount,
    isFetched: supplierAccountFetched,
  } = useSupplierStripeConnectAccount(supplierCompanyId, role === "supplier");

  const isShowByRole = useMemo(() => {
    if (role === "organiser") {
      if (!organiserCompanyId || !organiserHasVendrPackage) return false;
      // Don't flash the bar before the accounts query has resolved — an
      // empty `accounts` while loading would falsely report "missing".
      if (!organiserAccountsFetched) return false;

      const existingCurrencies = new Set(
        accounts.map((acc) => acc?.stripe?.currency).filter(Boolean)
      );
      const organiserHasNotSomeAccount =
        shows?.some((show) => !existingCurrencies.has(show?.currency)) ?? false;

      return organiserHasNotSomeAccount && !!shows?.length;
    }

    if (role === "supplier") {
      if (!supplierCompanyId || !supplierAccountFetched) return false;
      return !supplierAccount;
    }

    return false;
  }, [
    role,
    organiserCompanyId,
    organiserHasVendrPackage,
    organiserAccountsFetched,
    accounts,
    shows,
    supplierCompanyId,
    supplierAccountFetched,
    supplierAccount,
  ]);

  return isShowByRole && !hideSidebar;
};

export default useShowStripeConnectBar;
